import { Box, Grid, Stack, Typography } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoCard, SlidingPanelContent } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Loader } from 'core/elements';
import { IInfoCardLine } from 'core/types';
import { useGetPartSpecifications } from 'parts/hooks';
import { IPart, PartMainDetailsField } from 'parts/types';
import { Color, Level } from 'theme/enums';

import { EditPartParticularsButton } from './EditPartParticulars';
import { ImageGallery } from './ImageGallery';
import { buildFieldsByName, buildSpecificationSections, getStatusColor } from './utils';
import { PartDetailsTab } from '../enums';
import { usePartDetailsScrollToFocus } from '../hooks';

interface IProps {
  part?: IPart | null;
  enabled?: boolean;
  fullWidth?: boolean;
}

export const Specifications = ({ part, enabled = true, fullWidth = true }: IProps) => {
  const { t } = useTranslation();

  const { panelScrollAnchorRef } = usePartDetailsScrollToFocus(PartDetailsTab.SPECIFICATIONS);

  const requestPayload = useMemo(
    () => ({
      number: part?.number ?? '',
      manufacturer: part?.manufacturerUrlName ?? '',
    }),
    [part?.number, part?.manufacturerUrlName]
  );

  const isQueryEnabled =
    enabled && Boolean(requestPayload.number) && Boolean(requestPayload.manufacturer);

  const { record, isLoading, isError } = useGetPartSpecifications(requestPayload, isQueryEnabled);

  const fieldsByName = useMemo(
    () => buildFieldsByName(record?.partFieldValues ?? []),
    [record?.partFieldValues]
  );

  const { dimensionsSection, leftColumnSections, rightColumnSections } = useMemo(
    () =>
      buildSpecificationSections(record?.partFieldValues ?? [], {
        detailsLabel: t('labelDetails'),
        yesLabel: t('labelYes'),
        noLabel: t('labelNo'),
      }),
    [record?.partFieldValues, t]
  );

  const hasImages = (record?.imagesData?.length ?? 0) > 0;
  const hasAnyFieldData = hasImages || (record?.partFieldValues?.length ?? 0) > 0;

  const mainDetailsItems = useMemo<IInfoCardLine[]>(() => {
    const statusField = fieldsByName.get(PartMainDetailsField.STATUS);

    if (!statusField) {
      return [];
    }

    const rawStatus = Array.isArray(statusField.value) ? statusField.value[0] : statusField.value;

    return [
      {
        label: t('labelStatus'),
        value: (
          <Typography level={Level.TITLE_SM} color={getStatusColor(statusField.value)}>
            {rawStatus || DEFAULT_PLACEHOLDER}
          </Typography>
        ),
      },
    ];
  }, [fieldsByName, t]);

  const hasFirstColumn = hasImages || mainDetailsItems.length > 0 || dimensionsSection !== null;
  const hasLeftSpecColumn = leftColumnSections.length > 0;
  const hasRightSpecColumn = rightColumnSections.length > 0;
  const visibleColumnCount =
    Number(hasFirstColumn) + Number(hasLeftSpecColumn) + Number(hasRightSpecColumn);
  const columnLg = visibleColumnCount > 0 ? 12 / visibleColumnCount : 12;

  return (
    <Box ref={panelScrollAnchorRef} sx={{ display: 'contents' }}>
      <SlidingPanelContent
        title={t('titleSpecifications')}
        fullWidth={fullWidth}
        extra={!isLoading && !isError && record ? <EditPartParticularsButton part={part} /> : null}
        scrollableContent
      >
        {isLoading && <Loader />}

        {!isLoading && !hasAnyFieldData && (
          <Typography
            color={Color.NEUTRAL}
            level={Level.TITLE_SM}
            fontStyle="italic"
            sx={{ padding: 1 }}
          >
            {t('messageNoSpecifications')}
          </Typography>
        )}

        {!isLoading && hasAnyFieldData && (
          <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
            {hasFirstColumn && (
              <Grid xs={12} md={6} lg={columnLg} sx={{ minWidth: 0 }}>
                <Stack sx={{ gap: 2 }}>
                  <ImageGallery images={record?.imagesData} />

                  {mainDetailsItems.length > 0 && (
                    <InfoCard
                      title={t('labelMainDetails')}
                      titleLevel={Level.TITLE_LG}
                      items={mainDetailsItems}
                    />
                  )}

                  {dimensionsSection && (
                    <InfoCard
                      title={dimensionsSection.title}
                      titleLevel={Level.TITLE_LG}
                      items={dimensionsSection.items}
                    />
                  )}
                </Stack>
              </Grid>
            )}

            {hasLeftSpecColumn && (
              <Grid xs={12} md={6} lg={columnLg} sx={{ minWidth: 0 }}>
                <Stack sx={{ gap: 2 }}>
                  {leftColumnSections.map((section) => (
                    <InfoCard
                      key={section.key}
                      title={section.title}
                      titleLevel={Level.TITLE_LG}
                      items={section.items}
                    />
                  ))}
                </Stack>
              </Grid>
            )}

            {hasRightSpecColumn && (
              <Grid xs={12} md={6} lg={columnLg} sx={{ minWidth: 0 }}>
                <Stack sx={{ gap: 2 }}>
                  {rightColumnSections.map((section) => (
                    <InfoCard
                      key={section.key}
                      title={section.title}
                      titleLevel={Level.TITLE_LG}
                      items={section.items}
                    />
                  ))}
                </Stack>
              </Grid>
            )}
          </Grid>
        )}
      </SlidingPanelContent>
    </Box>
  );
};
